import Col from "antd/es/col";
import Row from "antd/es/row";
import styled from "styled-components";
import Text from "common/Typography/Text";
import { CSSProperties, ReactNode, useMemo } from "react";
import { ExpandCollapse } from "common/Icons/Icons";
import { SortingOrder } from "@shared/types";
import CaretRightOutlined from "@ant-design/icons/CaretRightOutlined";
import { v4 as uuid } from "uuid";
import {
  CardSorterColumn,
  CardSortersProps,
} from "@shared/common/CardSorters/types";

interface WebCardSortersProps extends CardSortersProps {
  style?: CSSProperties;
  sorterStyle?: CSSProperties;
  alignLastColumnRight?: boolean;
  className?: string;
}

const CardSorters = ({
  columnNames,
  columnSpans,
  style,
  showExpandCollapse,
  onExpandCollapseClick,
  sortOrder,
  sortColumnKey,
  columns,
  setSortColumnKey,
  setSortOrder,
  sorterStyle,
  alignLastColumnRight,
  className,
}: WebCardSortersProps) => {
  const iconID = useMemo(uuid, []);
  let _columns: CardSorterColumn[] =
    columns ||
    columnNames?.map((name, index) => ({
      title: name,
      span: columnSpans?.[index],
      key: index,
    })) ||
    [];

  const prefix = _columns?.find((column) => column.isPrefix);
  const filteredColumns = _columns.filter(
    (column) => !column.isPrefix && !!column.title
  );

  const onColumnClick = (columnKey: string | number) => {
    if (columnKey === sortColumnKey)
      setSortOrder?.(
        sortOrder === SortingOrder.DESCEND
          ? SortingOrder.ASCEND
          : SortingOrder.DESCEND
      );
    else {
      setSortColumnKey?.(columnKey);
      setSortOrder?.(SortingOrder.DESCEND);
    }
  };

  const getSorter = (
    column: CardSorterColumn,
    index: number,
    extraSorter?: ReactNode
  ) => {
    const style =
      index === filteredColumns.length - 1 && alignLastColumnRight
        ? { ...sorterStyle, justifyContent: "right" }
        : sorterStyle;
    const sorter = (
      <Sorter
        name={column.title}
        sortOrder={sortOrder}
        sorted={sortColumnKey === column.key}
        columnKey={column.key}
        onClick={onColumnClick}
        showSortIcon={!!setSortColumnKey && !!setSortOrder}
        style={style}
      />
    );

    return column.isPrefix ? (
      sorter
    ) : (
      <Col
        key={column.key}
        span={typeof column.span === "number" ? column.span : undefined}
        style={{ display: "flex", alignItems: "center" }}
      >
        {extraSorter}
        {!!extraSorter && <Separator />}
        {sorter}
      </Col>
    );
  };

  const getSorterColumn = (column: CardSorterColumn, index: number) => {
    if (index === 0 && prefix) {
      const prefixSorter = getSorter(prefix, index);
      return getSorter(column, index, prefixSorter);
    }
    return getSorter(column, index);
  };

  return (
    <SortersWrapper style={style} className={className}>
      <Row style={{ width: "100%" }}>
        {filteredColumns.map((column, index) => {
          if (!column.title) return;
          return getSorterColumn(column, index);
        })}
      </Row>
      {showExpandCollapse && (
        <ExpandCollapseIcon onClick={onExpandCollapseClick} id={iconID} />
      )}
    </SortersWrapper>
  );
};

export default CardSorters;

const SortersWrapper = styled.div`
  background-color: ${(p) => p.theme.colors.titleCardBgColor};
  border-radius: 8px;
  padding: 10px 12px;
  position: relative;
`;
const ExpandCollapseIcon = styled(ExpandCollapse)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 12px;
  cursor: pointer;
`;
const Separator = styled.div`
  top: 1px;
  height: 14px;
  width: 1px;
  background-color: ${(p) => p.theme.colors.hardBorderGrey};
  margin: 0 7px;
`;

interface SorterProps {
  name: ReactNode | ((selected: boolean) => JSX.Element);
  columnKey: string | number;
  sortOrder?: SortingOrder;
  sorted?: boolean;
  setSortColumnKey?: (key: string | number) => void;
  setSortOrder?: (sortOrder: SortingOrder) => void;
  onClick: (columnKey: string | number) => void;
  showSortIcon?: boolean;
  style?: CSSProperties;
}

const Sorter = ({
  name,
  columnKey,
  sortOrder,
  sorted,
  onClick,
  showSortIcon,
  style,
}: SorterProps) => {
  const getTitle = () => {
    if (typeof name === "function") {
      return name(!!sorted);
    }
    return name;
  };

  return (
    <SorterWrapper
      $sorted={sorted}
      onClick={() => onClick(columnKey)}
      style={style}
    >
      {getTitle()}
      {showSortIcon && (
        <CaretRightOutlined
          rotate={!sorted || sortOrder === SortingOrder.DESCEND ? 90 : 270}
          style={{ marginLeft: 4 }}
        />
      )}
    </SorterWrapper>
  );
};

const SorterWrapper = styled(Text).attrs((p) => ({
  type: p.$sorted ? "primary" : "medium",
  $size: p.theme.typography.fontSizes.M,
  as: "div",
}))<{ $sorted?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
`;
